import { GameState, SkiGameStateEnum } from '../ski';
import Tree from './tree';
import {
    HORIZON_Y,
    GROUND_Y,
    CENTER_X,
    TRACK_HALF_WIDTH,
    TRACK_EDGE,
    OBSTACLE_SPACING_METERS,
    DECOR_SPACING_METERS
} from './constants';

// Load tree image
const treeImg = new Image();
treeImg.src = '/tree.png';

export const treeImagePromise = new Promise<void>(resolve => {
    treeImg.onload = () => resolve();
});

// Spawner state
let lastObstacleDistance = 0;
let lastDecorDistance = 0;

class TreeSpawner {
    update(gameState: GameState) {
        if (gameState.gameState !== SkiGameStateEnum.racing) return;

        // Spawn obstacle trees
        while (gameState.distanceTraveled >= lastObstacleDistance + OBSTACLE_SPACING_METERS) {
            lastObstacleDistance += OBSTACLE_SPACING_METERS;
            this.spawnObstacle(gameState);
        }

        // Spawn decor trees
        while (gameState.distanceTraveled >= lastDecorDistance + DECOR_SPACING_METERS) {
            lastDecorDistance += DECOR_SPACING_METERS;
            this.spawnDecorTrees(gameState);
        }
    }

    spawnObstacle(gameState: GameState) {
        const spawnX = CENTER_X + (Math.random() - 0.5) * 2 * TRACK_HALF_WIDTH;
        const lane = Math.floor((spawnX - CENTER_X) / 15);

        const tree = new Tree({ spawnX, lane, isDecor: false }, treeImg);
        gameState.trees.push(tree);
    }

    spawnDecorTrees(gameState: GameState) {
        // Continuous line of trees at track edge
        const leftEdgeX = CENTER_X - TRACK_EDGE - 8 + (Math.random() - 0.5) * 6;
        gameState.trees.push(new Tree({ spawnX: leftEdgeX, lane: -3, isDecor: true }, treeImg));

        const rightEdgeX = CENTER_X + TRACK_EDGE + 8 + (Math.random() - 0.5) * 6;
        gameState.trees.push(new Tree({ spawnX: rightEdgeX, lane: 3, isDecor: true }, treeImg));

        // Additional trees further out (forest effect)
        for (let i = 0; i < 3; i++) {
            const leftX = CENTER_X - TRACK_EDGE - 20 - Math.random() * 35;
            gameState.trees.push(new Tree({ spawnX: leftX, lane: -5, isDecor: true }, treeImg));

            const rightX = CENTER_X + TRACK_EDGE + 20 + Math.random() * 35;
            gameState.trees.push(new Tree({ spawnX: rightX, lane: 5, isDecor: true }, treeImg));
        }
    }

    reset() {
        lastObstacleDistance = 0;
        lastDecorDistance = 0;
    }

    initializeTrees(gameState: GameState) {
        const initTree = (tree: Tree, y: number) => {
            tree.pos.y = y;
            tree.update(0, 0);
            gameState.trees.push(tree);
        };

        // Use variable step: small at top (dense), large at bottom (sparse)
        let y = HORIZON_Y;
        while (y < GROUND_Y - 50) {
            const progress = (y - HORIZON_Y) / (GROUND_Y - HORIZON_Y);

            // Step increases with progress: 4px at top, 25px at bottom
            const step = 4 + progress * 21;

            // Forest density: more trees at top
            const forestCount = Math.round(1 - progress * 0.8);

            // Decor trees on edges
            const leftEdgeX = CENTER_X - TRACK_EDGE - 8 + (Math.random() - 0.5) * 6;
            initTree(new Tree({ spawnX: leftEdgeX, lane: -3, isDecor: true }, treeImg), y);

            const rightEdgeX = CENTER_X + TRACK_EDGE + 8 + (Math.random() - 0.5) * 6;
            initTree(new Tree({ spawnX: rightEdgeX, lane: 3, isDecor: true }, treeImg), y);

            // Forest trees further out - more at top
            for (let i = 0; i < forestCount; i++) {
                const leftX = CENTER_X - TRACK_EDGE - 20 - Math.random() * 35;
                initTree(new Tree({ spawnX: leftX, lane: -5, isDecor: true }, treeImg), y + Math.random() * step);

                const rightX = CENTER_X + TRACK_EDGE + 20 + Math.random() * 35;
                initTree(new Tree({ spawnX: rightX, lane: 5, isDecor: true }, treeImg), y + Math.random() * step);
            }

            y += step;
        }
    }
}

export const treeSpawner = new TreeSpawner();
